import { ReactNode, useMemo } from 'react';
import { ComposedReportDefinition, printedSections } from '@/reports/compose';
import { ReportEvaluation, ReportValues, RunReportContext } from '@/reports/types';
import { BlockView, STANDARD_BLOCK_VIEWS } from '../blocks';
import { HeaderMeta, KeyValueData, ReportHeader, ReportSheet, ReportSummary, Section, SignOffs, SummaryGrid, TileData, signoffValues, useReportEditing } from '../primitives';
import { ReportViewProps } from '../types';

export interface ComposedHeader {
  kind: string;
  title: ReactNode;
  titleSub?: ReactNode;
  subtitle?: ReactNode;
  meta?: HeaderMeta[];
  /** the document-control grid under the summary */
  docControl?: KeyValueData[];
}

export interface ComposedDocumentProps extends ReportViewProps<ReportValues, ReportEvaluation, RunReportContext> {
  definition: ComposedReportDefinition;
  header: ComposedHeader;
  /** figures that frame the result, beside it in the summary */
  summaryTiles?: TileData[];
  footer?: ReactNode;
  /** views for the report's own block types, beside the SDK's */
  views?: Record<string, BlockView>;
}

/**
 * A composed report as it prints: the header, the result first, document
 * control, then the template's layout — each section numbered by the sheet,
 * each block drawn by its view — and the sign-offs after the last numbered
 * section. While the report is filled in, every block that asks for a field
 * stays in, empty or not.
 */
export function ComposedDocument({ definition, header, summaryTiles, footer, views, values, model, context, meta, onChange }: ComposedDocumentProps) {
  const { readOnly } = useReportEditing();
  const editing = !!onChange && !readOnly;
  const composition = useMemo(() => definition.compose(values, context), [definition, values, context]);
  const sections = printedSections(composition, { editing });
  const registry = { ...STANDARD_BLOCK_VIEWS, ...(views ?? {}) };
  const setField = onChange
    ? (key: string, value: string) => {
      const fields = values.fields && typeof values.fields === 'object' ? (values.fields as Record<string, string>) : {};
      onChange({ ...values, fields: { ...fields, [key]: value } });
    }
    : undefined;
  const status = meta.versionNumber
    ? `Version ${meta.versionNumber} · published ${meta.publishedAt ? new Date(meta.publishedAt).toLocaleDateString() : ''}${meta.publishedBy ? ` by ${meta.publishedBy}` : ''}`
    : 'Working copy — not yet published';
  const lastNumbered = sections.map((s) => s.section.numbered).lastIndexOf(true);
  const signoffs = <SignOffs specs={definition.signoffs} values={signoffValues(values)} onChange={onChange ? (names) => onChange({ ...values, signoffs: names }) : undefined} />;

  return (
    <ReportSheet footer={footer}>
      <ReportHeader kind={header.kind} verdict={model.verdict} verdictLabel={model.verdictLabel} status={status} title={header.title} titleSub={header.titleSub} subtitle={header.subtitle} meta={header.meta} />
      <ReportSummary evaluation={model} tiles={summaryTiles} />
      {header.docControl && header.docControl.length > 0 && <SummaryGrid items={header.docControl} />}
      {sections.map(({ section, blocks }, i) => (
        <div key={section.id}>
          <Section id={section.id} title={section.title || 'Untitled section'} numbered={section.numbered}>
            {blocks.length === 0 && <p className="small"><span className="unset">Nothing in this section yet.</span></p>}
            {blocks.map(({ block, definition: blockDefinition, input }) => {
              const View = registry[block.type];
              if (!View || !blockDefinition) return <p key={block.id} className="small"><span className="unset">This report can’t draw a “{block.type}” block.</span></p>;
              return <View key={block.id} input={input} evaluation={model} editing={editing} onField={setField} />;
            })}
          </Section>
          {i === lastNumbered && signoffs}
        </div>
      ))}
      {lastNumbered === -1 && signoffs}
    </ReportSheet>
  );
}
